import { useState, useMemo } from 'react';
import { calcPnlCalendar } from './analyzerCore';
import {
  Section, Kpi, InfoTip, LineChart, Pnl, Money, WinBar, JOURS,
  fmtHold, fmtUsd, fmtDateTime, fmtDay, heureLabel, shortAddr,
} from './anUi';
import { TokensTable } from './Tables';

function Heatmaps({ heatmap }) {
  const maxAbs = (arr) => Math.max(...arr.map(x => Math.abs(x.pnl)), 0.01);
  const cellStyle = (x, max) => {
    if (x.count === 0) return { background: 'var(--bg-3)' };
    const a = 0.15 + (Math.abs(x.pnl) / max) * 0.6;
    return { background: x.pnl >= 0 ? `rgba(34,197,94,${a})` : `rgba(239,68,68,${a})` };
  };
  const mh = maxAbs(heatmap.heuresPnl), mj = maxAbs(heatmap.joursPnl);
  const tip = (label, x) => `${label} : ${x.count} trades, ${x.pnl >= 0 ? '+' : ''}$${x.pnl.toFixed(0)}`;
  return (
    <div className="an-heat">
      <div>
        <div className="an-heat-cap">Par heure — meilleure heure : <b>{heureLabel(heatmap.bestHeure)}</b></div>
        <div className="an-heat-row an-heat-row--24">
          {heatmap.heuresPnl.map((x, i) => (
            <div key={i} className="an-heat-col" title={tip(`${i}h`, x)}>
              <div className="an-heat-cell" style={cellStyle(x, mh)} />
              {i % 4 === 0 && <div className="an-heat-lbl">{i}h</div>}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="an-heat-cap">Par jour — meilleur jour : <b>{heatmap.bestJour != null ? JOURS[heatmap.bestJour] : '—'}</b></div>
        <div className="an-heat-row">
          {heatmap.joursPnl.map((x, i) => (
            <div key={i} className="an-heat-col" title={tip(JOURS[i], x)}>
              <div className="an-heat-cell an-heat-cell--tall" style={cellStyle(x, mj)} />
              <div className="an-heat-lbl">{JOURS[i]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CalendarBlock({ positions }) {
  const [offset, setOffset] = useState(0);
  const target = new Date();
  target.setDate(1);
  target.setMonth(target.getMonth() + offset);
  const m = target.getMonth(), y = target.getFullYear();
  const map = useMemo(() => calcPnlCalendar(positions, m, y), [positions, m, y]);
  const values = Object.values(map);
  const total = values.reduce((s, v) => s + v, 0);
  const prof = values.filter(v => v > 0).length;
  const best = Math.max(0, ...values);
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7;
  const nbDays = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= nbDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const monthName = target.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase());
  return (
    <div className="an-cal">
      <div className="an-cal-nav">
        <button className="an-page-btn" onClick={() => setOffset(o => o - 1)}>‹</button>
        <span className="an-cal-month">{monthName}</span>
        <button className="an-page-btn" disabled={offset >= 0} onClick={() => setOffset(o => Math.min(0, o + 1))}>›</button>
      </div>
      <div className="an-cal-summary">
        <span>PNL total <Money value={total} /></span>
        <span>Jours profitables <b>{values.length ? `${prof}/${values.length}` : '0/0'}</b></span>
        <span>Meilleur jour <Money value={best > 0 ? best : null} /></span>
      </div>
      <div className="an-cal-grid">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i} className="an-cal-dh">{d}</div>)}
        {cells.map((d, i) => {
          const v = d ? map[d] : undefined;
          const cls = v === undefined ? '' : v >= 0 ? ' an-cal-cell--pos' : ' an-cal-cell--neg';
          return (
            <div key={i} className={`an-cal-cell${d ? '' : ' an-cal-cell--empty'}${cls}`}>
              {d && <span className="an-cal-num">{d}</span>}
              {v !== undefined && <span className="an-cal-val">{fmtUsd(v, true)}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OverviewTab({ d, eurRate }) {
  const bal = d.solBalance;
  const stat = (label, node, tip) => (
    <div className="an-kpi" key={label}>
      <div className="an-kpi-label">{label}{tip && <InfoTip text={tip} />}</div>
      <div className="an-kpi-value">{node}</div>
    </div>
  );
  return (
    <>
      <Section icon="🪪" title="Identité">
        <div className="an-id">
          <div>
            <div className="an-id-addr">{shortAddr(d.address)} <span className="an-chain">SOLANA</span></div>
            <div className="an-muted an-small">
              {d.dateRange ? `${fmtDay(d.dateRange.from)} → ${fmtDay(d.dateRange.to)}` : 'Aucune transaction'}
              {' · '}Dernière analyse : {fmtDateTime(d.fetchedAt)}
            </div>
          </div>
          <span className={`an-tag an-tag--${d.tag.color}`}>{d.tag.label}</span>
        </div>
      </Section>

      <Section icon="📋" title="Résumé wallet">
        <div className="an-kpis">
          {stat('Transactions', d.txCount, 'Nombre de transactions analysées (500 max)')}
          {stat('Swaps', d.swapCount, "Nombre d'échanges détectés")}
          {stat('Jours actifs', d.daysActive, 'Jours depuis la plus ancienne transaction analysée')}
          {stat('Fréquence', `${d.tradesPerDay.toFixed(1)} /jour`, 'Swaps par jour')}
          {stat('ROI estimé', <Pnl value={d.overallRoi} />, 'Retour sur investissement des positions fermées')}
          {stat('Win Rate', d.totalTrades > 0 ? <WinBar value={d.winRate} /> : '—', '% de trades fermés gagnants')}
        </div>
        {d.ignoredDust > 0 && <div className="an-note">{d.ignoredDust} position{d.ignoredDust > 1 ? 's' : ''} ignorée{d.ignoredDust > 1 ? 's' : ''} (coût &lt; 0,01 SOL — dust/airdrop).</div>}
      </Section>

      <Section icon="📊" title="Stats avancées">
        <div className="an-kpis">
          <Kpi label="Meilleur trade" value={d.bestTrade ? <>{d.bestTrade.symbol} <Pnl value={d.bestTrade.roi} digits={0} /></> : '—'} />
          <Kpi label="Pire trade" value={d.worstTrade ? <>{d.worstTrade.symbol} <Pnl value={d.worstTrade.roi} digits={0} /></> : '—'} />
          <Kpi label="Ratio R/R" value={d.rrRatio != null ? d.rrRatio.toFixed(2) : '—'} tone={d.rrRatio != null ? (d.rrRatio >= 1 ? 'pos' : 'neg') : ''} tip="Gain moyen / perte moyenne" />
          <Kpi label="Streak max" value={d.maxStreak > 0 ? `${d.maxStreak} consécutifs` : '—'} tone="pos" />
          <Kpi label="Durée moy." value={fmtHold(d.dureeMoyMin)} />
          <Kpi label="Meilleure heure" value={heureLabel(d.heatmap.bestHeure)} tone="pos" />
          <Kpi label="Meilleur jour" value={d.heatmap.bestJour != null ? JOURS[d.heatmap.bestJour] : '—'} tone="pos" />
        </div>
      </Section>

      {d.pnlChart.length > 2 && (
        <Section icon="📈" title="Évolution du PNL">
          <LineChart values={d.pnlChart.map(p => p.pnl)} baseline={0} />
          <div className="an-chart-foot">
            <span>{fmtDay(d.pnlChart[0].ts)}</span>
            <span>PNL final <Money value={d.pnlUsd} /></span>
            <span>{fmtDay(d.pnlChart[d.pnlChart.length - 1].ts)}</span>
          </div>
        </Section>
      )}

      <Section icon="🔥" title="Heatmap des performances"><Heatmaps heatmap={d.heatmap} /></Section>

      <Section icon="🏆" title="Performance globale">
        <div className="an-kpis">
          <Kpi label="ROI total" value={<Pnl value={d.overallRoi} />} />
          <Kpi label="PNL total" value={<><Money value={d.totalTrades > 0 ? d.pnlUsd : null} />
            {d.totalTrades > 0 && <div className="an-muted an-small">{d.pnlSol.toFixed(3)} SOL{eurRate ? ` ≈ €${(d.pnlUsd * eurRate).toFixed(0)}` : ''}</div>}</>} />
          <Kpi label="Trades" value={d.totalTrades > 0 ? d.totalTrades : d.swapCount > 0 ? '0 fermé' : '0'} />
          <Kpi label="Win rate" value={d.totalTrades > 0 ? <WinBar value={d.winRate} /> : '—'} />
          <Kpi label="Gain moyen" value={d.avgGainUsd > 0 ? fmtUsd(d.avgGainUsd) : '—'} tone="pos" />
          <Kpi label="Perte moyenne" value={d.avgLossUsd > 0 ? fmtUsd(d.avgLossUsd) : '—'} tone="neg" />
        </div>
      </Section>

      <Section icon="⏱" title="Timing du wallet">
        <div className="an-kpis">
          <Kpi label="Balance SOL" value={bal != null
            ? <>{bal.toFixed(4)} SOL<div className="an-muted an-small">${(bal * d.solPrice).toFixed(0)}{eurRate ? ` · €${(bal * d.solPrice * eurRate).toFixed(0)}` : ''}</div></>
            : 'Non disponible'} />
          <Kpi label="Tokens tradés" value={`${d.positions.length} tokens`} />
          <Kpi label="Jours actif" value={`${d.daysActive} jours`} />
          <Kpi label="Fréquence" value={`${d.tradesPerDay.toFixed(1)} trades/jour`} />
          <Kpi label="Détection bot" value={<>{d.isBot ? 'Bot probable' : 'Humain probable'}
            <div className="an-bot"><div className="an-bot-fill" style={{ width: `${d.botScore}%`, background: d.isBot ? '#ef4444' : '#22c55e' }} /></div>
            <div className="an-muted an-small">botScore {d.botScore}/100</div></>} />
        </div>
      </Section>

      <Section icon="🎭" title="Profil de trading">
        <div className="an-profile">
          <span className={`an-tag an-tag--${d.tag.color}`}>{d.tag.label}</span>
          <span className="an-muted">Tendance : <b>{d.tendance}</b></span>
        </div>
      </Section>

      <Section icon="💎" title="Meilleurs tokens" right={d.tokenRows.length > 0 && <span className="an-count">{d.tokenRows.length}</span>}>
        {d.tokenRows.length === 0
          ? <p className="an-empty-inline">Aucun swap détecté.</p>
          : <TokensTable rows={d.tokenRows.slice(0, 50)} variant="best" />}
      </Section>

      <Section icon="🗓" title="PNL Calendar"><CalendarBlock positions={d.positions} /></Section>
    </>
  );
}
